import { __extends } from "tslib";
import { BitmapFilter, GradientArrays } from './BitmapFilter';
import { BitmapFilterType } from './BitmapFilterType';
import { clamp, isNullOrUndefined } from '@awayfl/swf-loader';
import { Errors, axCoerceString } from '@awayfl/avm2';
/**
 * Copyright 2014 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// Class: GradientGlowFilter
var GradientGlowFilter = /** @class */ (function (_super) {
    __extends(GradientGlowFilter, _super);
    function GradientGlowFilter(distance, angle, colors, alphas, ratios, blurX, blurY, strength, quality, type, knockout) {
        if (distance === void 0) { distance = 4; }
        if (angle === void 0) { angle = 45; }
        if (colors === void 0) { colors = null; }
        if (alphas === void 0) { alphas = null; }
        if (ratios === void 0) { ratios = null; }
        if (blurX === void 0) { blurX = 4; }
        if (blurY === void 0) { blurY = 4; }
        if (strength === void 0) { strength = 1; }
        if (quality === void 0) { quality = 1; }
        if (type === void 0) { type = 'inner'; }
        if (knockout === void 0) { knockout = false; }
        var _this = _super.call(this) || this;
        _this.distance = distance;
        _this.angle = angle;
        GradientArrays.sanitize(colors ? colors.value : null, alphas ? alphas.value : null, ratios ? ratios.value : null);
        _this._colors = GradientArrays.colors;
        _this._alphas = GradientArrays.alphas;
        _this._ratios = GradientArrays.ratios;
        _this.blurX = blurX;
        _this.blurY = blurY;
        _this.strength = strength;
        _this.quality = quality;
        _this.type = type;
        _this.knockout = knockout;
        return _this;
    }
    GradientGlowFilter.FromUntyped = function (obj, sec) {
        // obj.colors is an array of RGBA colors.
        // The RGB and alpha parts must be separated into colors and alphas arrays.
        var colors = [];
        var alphas = [];
        for (var i = 0; i < obj.colors.length; i++) {
            var color = obj.colors[i];
            colors.push(color >>> 8);
            alphas.push(color & 0xff) / 0xff;
        }
        // type is derived from obj.onTop and obj.innerShadow
        // obj.onTop true: type is FULL
        // obj.inner true: type is INNER
        // neither true: type is OUTER
        var type = BitmapFilterType.OUTER;
        if (obj.onTop) {
            type = BitmapFilterType.FULL;
        }
        else if (obj.inner) {
            type = BitmapFilterType.INNER;
        }
        // obj.angle is represented in radians, the api needs degrees
        var angle = obj.angle * 180 / Math.PI;
        return new sec.flash.filters.GradientGlowFilter(obj.distance, angle, 
        // Boxing these is obviously not ideal, but everything else is just annoying.
        this.sec.createArrayUnsafe(colors), this.sec.createArrayUnsafe(alphas), this.sec.createArrayUnsafe(obj.ratios), obj.blurX, obj.blurY, obj.strength, obj.quality, type, obj.knockout);
    };
    Object.defineProperty(GradientGlowFilter.prototype, "distance", {
        get: function () {
            return this._distance;
        },
        set: function (value) {
            this._distance = +value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(GradientGlowFilter.prototype, "angle", {
        get: function () {
            return this._angle;
        },
        set: function (value) {
            this._angle = +value % 360;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(GradientGlowFilter.prototype, "colors", {
        get: function () {
            return this.sec.createArrayUnsafe(this._colors.concat());
        },
        set: function (value) {
            if (isNullOrUndefined(value)) {
                this.sec.throwError('TypeError', Errors.NullPointerError, 'colors');
            }
            this._colors = GradientArrays.sanitizeColors(value.value);
            var len = this._colors.length;
            this._alphas = GradientArrays.sanitizeAlphas(this._alphas, len, len);
            this._ratios = GradientArrays.sanitizeRatios(this._ratios, len, len);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(GradientGlowFilter.prototype, "alphas", {
        get: function () {
            return this.sec.createArrayUnsafe(this._alphas.concat());
        },
        set: function (value) {
            if (isNullOrUndefined(value)) {
                this.sec.throwError('TypeError', Errors.NullPointerError, 'alphas');
            }
            GradientArrays.sanitize(this._colors, value.value, this._ratios);
            this._colors = GradientArrays.colors;
            this._alphas = GradientArrays.alphas;
            this._ratios = GradientArrays.ratios;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(GradientGlowFilter.prototype, "ratios", {
        get: function () {
            return this.sec.createArrayUnsafe(this._ratios.concat());
        },
        set: function (value_) {
            if (isNullOrUndefined(value_)) {
                this.sec.throwError('TypeError', Errors.NullPointerError, 'ratios');
            }
            GradientArrays.sanitize(this._colors, this._alphas, value_.value);
            this._colors = GradientArrays.colors;
            this._alphas = GradientArrays.alphas;
            this._ratios = GradientArrays.ratios;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(GradientGlowFilter.prototype, "blurX", {
        get: function () {
            return this._blurX;
        },
        set: function (value) {
            this._blurX = clamp(+value, 0, 255);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(GradientGlowFilter.prototype, "blurY", {
        get: function () {
            return this._blurY;
        },
        set: function (value) {
            this._blurY = clamp(+value, 0, 255);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(GradientGlowFilter.prototype, "knockout", {
        get: function () {
            return this._knockout;
        },
        set: function (value) {
            this._knockout = !!value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(GradientGlowFilter.prototype, "quality", {
        get: function () {
            return this._quality;
        },
        set: function (value /*int*/) {
            this._quality = clamp(value | 0, 0, 15);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(GradientGlowFilter.prototype, "strength", {
        get: function () {
            return this._strength;
        },
        set: function (value) {
            this._strength = clamp(+value, 0, 255);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(GradientGlowFilter.prototype, "type", {
        get: function () {
            return this._type;
        },
        set: function (value) {
            value = axCoerceString(value);
            if (value === null) {
                this.sec.throwError('TypeError', Errors.NullPointerError, 'type');
            }
            else {
                if (value === BitmapFilterType.INNER || value === BitmapFilterType.OUTER) {
                    this._type = value;
                }
                else {
                    this._type = BitmapFilterType.FULL;
                }
            }
        },
        enumerable: false,
        configurable: true
    });
    GradientGlowFilter.prototype.clone = function () {
        return new this.sec.flash.filters.GradientGlowFilter(this._distance, this._angle, this.colors, this.alphas, this.ratios, this._blurX, this._blurY, this._strength, this._quality, this._type, this._knockout);
    };
    GradientGlowFilter.classInitializer = null;
    return GradientGlowFilter;
}(BitmapFilter));
export { GradientGlowFilter };
